import { Info, Sparkle } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Card } from '@/components/ui/card'
import { PricingFeatureItem } from './PricingFeatureItem'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { useTranslation } from '@/hooks/useTranslation'

interface PricingCardProps {
	name: string
	price: number | string
	isMostPopular: boolean
	features: string[]
	className?: string
	hideButton?: boolean
	additionalPrice?: string
	isLoading?: boolean
	seatSelector?: React.ReactNode
	priceSubtext?: string
	customButton?: React.ReactNode
	addonToggle?: {
		id: string
		checked: boolean
		onCheckedChange: (checked: boolean) => void
		title: string
		priceLabel: string
		description: string
	}
}

export function PricingCard({
	name,
	price,
	isMostPopular,
	features,
	className,
	hideButton,
	additionalPrice,
	isLoading = false,
	seatSelector,
	priceSubtext,
	customButton,
	addonToggle,
}: PricingCardProps) {
	const { t } = useTranslation()
	const isEnterprise = typeof price === 'string'
	const renderFeature = (feature: string, index: number) => (
		<PricingFeatureItem key={`${feature}-${index}`} text={feature} />
	)

	return (
		<Card
			className={cn(
				'relative rounded-2xl shadow-none flex flex-col justify-between py-8 px-7 gap-7 border',
				isMostPopular && 'border-primary shadow-lg',
				className
			)}>
			<div className="flex flex-col gap-3">
				<div className="flex items-center justify-between">
					<h3 className="font-heading text-2xl font-semibold text-foreground">
						{name}
					</h3>
					{isMostPopular && (
							<span className="inline-flex items-center gap-1 rounded-full bg-[#6547D9] px-3 py-1 text-center text-sm font-semibold text-white">
								<Sparkle size={16} className="fill-white" />
							{t('pricingCard.popular')}
						</span>
					)}
				</div>
				<div>
					{isLoading ? (
						<div className="flex items-center">
							<Skeleton className="h-12 w-24" />
							{isMostPopular === true && <Skeleton className="h-6 w-12 ml-2" />}
							<Skeleton className="h-6 w-20 ml-2" />
						</div>
					) : typeof price === 'number' ? (
						<div className="flex items-baseline gap-2">
							<span className="font-heading text-5xl font-semibold text-primary">
								{price === 0 ? t('pricingCard.free') : `$${price}`}
							</span>
							<span className="text-muted-foreground font-semibold text-xl font-heading">
								{price === 0 ? t('pricingCard.forever') : t('pricingCard.perMonth')}
							</span>
							{priceSubtext && (
								<span className="text-muted-foreground text-xs">
									({priceSubtext})
								</span>
							)}
						</div>
					) : (
						<span className="font-heading text-3xl font-semibold text-foreground">{price}</span>
					)}
				</div>
			</div>
			<div className="flex flex-col flex-1 gap-8">
					<ul className="space-y-2">
						{features.map((feature, index) => renderFeature(feature, index))}
					</ul>
				{additionalPrice && (
					<div className="text-sm text-muted-foreground text-center space-y-1">
						{additionalPrice.split('\n').map((line, index) => (
							<p key={index}>{line}</p>
						))}
					</div>
				)}
			</div>
			<div className="flex flex-col gap-8">
				{addonToggle && (
					<div className="flex items-center justify-between gap-3 border-t border-[#E7E2EF] pt-5">
						<div className="min-w-0 space-y-0.5">
							<div className="flex items-center gap-1.5">
								<label
									htmlFor={addonToggle.id}
									className="cursor-pointer border-b border-dotted border-[#17131F]/40 text-sm font-semibold text-[#17131F]">
									{addonToggle.title}
								</label>
								<Tooltip>
									<TooltipTrigger asChild>
										<button
											type="button"
											className="inline-flex text-muted-foreground transition-colors hover:text-foreground"
											aria-label={addonToggle.description}>
											<Info className="h-3.5 w-3.5" />
										</button>
									</TooltipTrigger>
									<TooltipContent className="max-w-xs">
										{addonToggle.description}
									</TooltipContent>
								</Tooltip>
							</div>
							<p className="text-sm text-muted-foreground">
								{addonToggle.priceLabel}
							</p>
						</div>
						<Switch
							id={addonToggle.id}
							checked={addonToggle.checked}
							onCheckedChange={addonToggle.onCheckedChange}
							aria-label={addonToggle.title}
						/>
					</div>
				)}
				{seatSelector}
				{!hideButton && (
					customButton || (
						<Button
							size="lg"
							asChild
							variant={isEnterprise ? 'outline' : 'default'}
								className={cn(
									'font-semibold text-lg py-4',
									!isEnterprise &&
										'bg-[#6547D9] text-white hover:bg-[#5638C7]'
								)}>
							{isEnterprise ? (
								<a href="mailto:contact@feedbask.com">
									{t('pricingCard.contactUs')}
								</a>
							) : (
								<Link
									to="/auth?redirect=pricing"
									data-price-id={price === 0 ? 'free' : 'starter'}>
									{price === 0
										? t('pricingCard.getStarted')
										: t('pricingCard.getStartedPro')}
								</Link>
							)}
						</Button>
					)
				)}
			</div>
		</Card>
	)
}
